import { create } from 'zustand'

/** New-deploy detection. The bundle's boot version (`__APP_VERSION__`, baked
 *  by vite.config.ts) is compared against the deployed `version.json` every
 *  few minutes and whenever the tab regains focus; a mismatch surfaces the
 *  reload banner. All fetch failures are silent — offline or a mid-deploy
 *  asset swap must never error the UI. Dev builds all report 'dev', so they
 *  never prompt. */
interface VersionState {
  /** The deployed version when it differs from the boot one, else null. */
  updateVersion: string | null
  /** The version the operator dismissed — the banner stays hidden until the
   *  NEXT version change. */
  dismissed: string | null
  check: () => Promise<void>
  dismiss: () => void
}

const BOOT_VERSION = __APP_VERSION__
const POLL_MS = 5 * 60_000

export const useVersionStore = create<VersionState>((set, get) => ({
  updateVersion: null,
  dismissed: null,
  check: async () => {
    if (BOOT_VERSION === 'dev') return
    try {
      const res = await fetch('/version.json', { cache: 'no-store' })
      if (!res.ok) return
      const data = (await res.json()) as { version?: unknown }
      if (typeof data.version !== 'string' || data.version === 'dev') return
      set({ updateVersion: data.version === BOOT_VERSION ? null : data.version })
    } catch {
      // Offline / mid-deploy — try again on the next tick.
    }
  },
  dismiss: () => set({ dismissed: get().updateVersion }),
}))

/** Start the polling loop + focus/visibility triggers. Idempotent. */
export function startVersionPolling(): void {
  const { check } = useVersionStore.getState()
  void check()
  setInterval(() => void useVersionStore.getState().check(), POLL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void useVersionStore.getState().check()
  })
  window.addEventListener('focus', () => void useVersionStore.getState().check())
}
